/* =====================================================================
   The Whole Hope. Price sync v2.3.3
   Watches the entire document for selling_plan changes (across multiple
   forms) and variant_id changes, updates the main price display.

   Strategy:
   1. Capture the per-variant OTP baseline price from Shopify product
      JSON if available, else from initial DOM render.
   2. Discount math: when ANY selling_plan input on the page is non-empty,
      show variant_price * 0.9 (TWH's 10% subscription discount).
   3. Listen via:
       a) document-wide change event (capture phase)
       b) MutationObserver on body for attribute changes on
          [name="selling_plan"] and [name="id"]
       c) 500ms polling safety net
   ===================================================================== */

(function() {
  'use strict';

  if (window.__twhPriceSyncLoaded) return;
  window.__twhPriceSyncLoaded = true;

  var DISCOUNT_PCT = 10; // TWH subscription discount
  var POLL_MS = 500;

  function init() {
    var priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) {
      console.warn('[twh-price-sync] no [data-product-price] element');
      return;
    }

    // Try to read the product JSON to get authoritative variant prices
    var productData = null;
    var jsonSelectors = [
      'script[type="application/json"][data-product-json]',
      'script[type="application/json"][data-product]',
      'script[id^="ProductJson-"]',
      'script[data-product-json]'
    ];
    for (var i = 0; i < jsonSelectors.length; i++) {
      var el = document.querySelector(jsonSelectors[i]);
      if (el) {
        try {
          productData = JSON.parse(el.textContent);
          break;
        } catch (e) {}
      }
    }

    function formatMoneyCents(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try { return window.Shopify.formatMoney(cents); } catch (e) {}
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function getCurrentVariantId() {
      // Try main product form first
      var inputs = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value) return String(inputs[i].value);
      }
      // Fall back to URL param
      var urlMatch = location.search.match(/[?&]variant=(\d+)/);
      return urlMatch ? urlMatch[1] : null;
    }

    function getAnySellingPlanValue() {
      // Returns truthy plan ID if ANY selling_plan input has a value
      var inputs = document.querySelectorAll('[name="selling_plan"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value && inputs[i].value !== '0') return inputs[i].value;
      }
      return null;
    }

    // Variant ID -> OTP price (string)
    var variantPriceMap = {};
    if (productData && productData.variants) {
      productData.variants.forEach(function(v) {
        variantPriceMap[String(v.id)] = formatMoneyCents(v.price);
      });
    }

    // Capture the initial DOM-rendered price as a fallback baseline
    var initialPriceText = priceEl.textContent.trim();
    var initialVariantId = getCurrentVariantId();
    if (initialVariantId && !variantPriceMap[initialVariantId]) {
      // If product JSON wasn't available, AND no selling_plan is currently
      // active, seed with the current rendered price (it IS the OTP price).
      // If a selling plan IS active, the rendered price is already wrong
      // and we can't reliably back-calculate, so seed with the displayed
      // price anyway and let the customer's first variant change correct it.
      variantPriceMap[initialVariantId] = initialPriceText;
    }

    function computeDiscountedPrice(otpPriceText) {
      var m = otpPriceText.match(/[\d,]+\.?\d*/);
      if (!m) return otpPriceText;
      var num = parseFloat(m[0].replace(/,/g, ''));
      if (isNaN(num)) return otpPriceText;
      var discounted = (num * (1 - DISCOUNT_PCT / 100));
      var discountedStr = discounted.toFixed(2);
      return otpPriceText.replace(m[0], discountedStr);
    }

    var lastRendered = '';
    var lastStickerState = null;
    var lastNudgeState = null;
    var lastCompareState = null;
    var lastCompareText = '';
    var stickerEl = document.querySelector('[data-twh-savings-sticker]');
    var otpNudgeEl = document.querySelector('[data-twh-otp-nudge]');
    var comparePriceEl = document.querySelector('[data-twh-compare-price]');

    // Returns true if the current variant has selling_plan_allocations
    // (i.e., supports subscriptions). If the product JSON wasn't loaded,
    // returns true (assume yes — fail open).
    function isCurrentVariantSubEligible() {
      if (!productData || !productData.variants) return true;
      var vid = getCurrentVariantId();
      if (!vid) return true;
      for (var i = 0; i < productData.variants.length; i++) {
        var v = productData.variants[i];
        if (String(v.id) === String(vid)) {
          return !!(v.selling_plan_allocations && v.selling_plan_allocations.length > 0);
        }
      }
      return true;
    }

    function sync() {
      var variantId = getCurrentVariantId();
      var sellingPlan = getAnySellingPlanValue();

      // CRITICAL: if the current variant doesn't support subscriptions
      // (e.g., the 2-Pack), force OTP state regardless of any stale
      // selling_plan input value. This prevents showing the 10% discount,
      // starburst, or compare price on a non-sub-eligible variant.
      if (!isCurrentVariantSubEligible()) {
        sellingPlan = null;
      }

      var otpPrice = (variantId && variantPriceMap[variantId]) || initialPriceText;
      var newText = sellingPlan ? computeDiscountedPrice(otpPrice) : otpPrice;
      if (newText !== lastRendered) {
        priceEl.textContent = newText;
        lastRendered = newText;
      }

      // Toggle the savings sticker (active when subscribe is selected)
      if (stickerEl) {
        var stickerActive = sellingPlan ? 'true' : 'false';
        if (lastStickerState !== stickerActive) {
          stickerEl.dataset.active = stickerActive;
          stickerEl.setAttribute('aria-hidden', stickerActive === 'true' ? 'false' : 'true');
          lastStickerState = stickerActive;
        }
      }

      // Toggle the OTP nudge pill (active when OTP is selected, inverse of sticker).
      // Use the `inert` attribute instead of aria-hidden to avoid the a11y
      // violation when the focused button is being hidden. Blur first if focused.
      if (otpNudgeEl) {
        var nudgeActive = sellingPlan ? 'false' : 'true';
        if (lastNudgeState !== nudgeActive) {
          if (nudgeActive === 'false' && document.activeElement === otpNudgeEl) {
            try { otpNudgeEl.blur(); } catch (e) {}
          }
          otpNudgeEl.dataset.active = nudgeActive;
          if (nudgeActive === 'true') {
            otpNudgeEl.removeAttribute('inert');
            otpNudgeEl.setAttribute('aria-hidden', 'false');
          } else {
            otpNudgeEl.setAttribute('inert', '');
            otpNudgeEl.setAttribute('aria-hidden', 'true');
          }
          lastNudgeState = nudgeActive;
        }
      }

      // Toggle the compare-at strikethrough price (active when subscribe is selected,
      // showing the OTP price as the reference). Sync its text to current variant OTP.
      if (comparePriceEl) {
        var compareActive = sellingPlan ? 'true' : 'false';
        if (lastCompareText !== otpPrice) {
          comparePriceEl.textContent = otpPrice;
          lastCompareText = otpPrice;
        }
        if (lastCompareState !== compareActive) {
          comparePriceEl.dataset.active = compareActive;
          comparePriceEl.setAttribute('aria-hidden', compareActive === 'true' ? 'false' : 'true');
          lastCompareState = compareActive;
        }
      }
    }

    // 1. Change event capture (catches Shopify variant + Recharge selling_plan)
    document.addEventListener('change', function(e) {
      if (!e.target) return;
      var name = e.target.name || '';
      if (name === 'selling_plan' || name === 'id' || name.indexOf('options[') === 0) {
        setTimeout(sync, 30);
      }
    }, true);

    // 2. MutationObserver on body for attribute changes on selling_plan/id inputs
    var observer = new MutationObserver(function(mutations) {
      var relevant = false;
      mutations.forEach(function(m) {
        if (m.type === 'attributes' && m.target && m.target.name &&
            (m.target.name === 'selling_plan' || m.target.name === 'id')) {
          relevant = true;
        }
        if (m.type === 'childList' && m.addedNodes && m.addedNodes.length) {
          for (var i = 0; i < m.addedNodes.length; i++) {
            var n = m.addedNodes[i];
            if (n.nodeType === 1 && n.querySelector && n.querySelector('[name="selling_plan"]')) {
              relevant = true;
              break;
            }
          }
        }
      });
      if (relevant) sync();
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['value']
    });

    // 3. Polling safety net
    setInterval(sync, POLL_MS);

    // 4. Initial sync (give Recharge a beat to mount)
    sync();
    setTimeout(sync, 300);
    setTimeout(sync, 800);
    setTimeout(sync, 1500);

    // 5. Pill click handler: switch from OTP to Subscribe.

    // Deep query across all open shadow DOMs. Recharge v2 widget renders
    // its UI inside a shadow root (the `part` attribute on the input
    // confirms this), so plain document.querySelector cannot reach it.
    function deepQuerySelector(selector, root) {
      root = root || document;
      try {
        var direct = root.querySelector(selector);
        if (direct) return direct;
      } catch (e) {}
      var allElements;
      try {
        allElements = root.querySelectorAll('*');
      } catch (e) {
        return null;
      }
      for (var i = 0; i < allElements.length; i++) {
        var el = allElements[i];
        if (el.shadowRoot) {
          var nested = deepQuerySelector(selector, el.shadowRoot);
          if (nested) return nested;
        }
      }
      return null;
    }

    function deepFindAllShadowHosts() {
      var hosts = [];
      var all = document.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        if (all[i].shadowRoot) hosts.push(all[i]);
      }
      return hosts;
    }

    function trySelectFourPack() {
      var radios = document.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        var r = radios[i];
        var v = (r.value || '').toLowerCase();
        if ((v === '4 pack' || v === '4-pack' || v === '4_pack') && !r.checked) {
          r.click();
          r.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
      return false;
    }

    // Find the clickable Subscribe element inside the Recharge widget.
    // Aggressive multi-strategy search.
    function findRechargeSubscribeElement() {
      var roots = document.querySelectorAll(
        '.recharge-subscription-widget, ' +
        '[id*="recharge_subscriptions"], ' +
        '[id*="recharge-subscription"], ' +
        '[class*="recharge-subscription"], ' +
        '[id*="shopify-block"][id*="recharge"]'
      );

      function isClickable(el) {
        if (!el) return false;
        var tag = el.tagName;
        var cs = window.getComputedStyle ? window.getComputedStyle(el) : {};
        return (
          tag === 'LABEL' || tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' ||
          el.getAttribute('role') === 'radio' ||
          el.getAttribute('role') === 'button' ||
          el.getAttribute('role') === 'option' ||
          cs.cursor === 'pointer' ||
          el.hasAttribute('data-selling-plan-id') ||
          el.hasAttribute('data-selling-plan') ||
          el.hasAttribute('data-rc-option') ||
          el.hasAttribute('data-recharge-mode') ||
          el.hasAttribute('tabindex')
        );
      }

      function walkUpToClickable(el, stopAt) {
        var cur = el;
        while (cur && cur !== stopAt) {
          if (isClickable(cur)) return cur;
          cur = cur.parentElement;
        }
        return el; // fallback to original
      }

      // 0. EXACT Recharge v2 selectors (discovered from real DOM inspection).
      //    Try the global document first — the input lives in the light DOM
      //    even though parts of the widget use shadow DOM. Returning the
      //    LABEL is preferable because clicking a label toggles its radio
      //    AND triggers Recharge's wrapping event handlers.
      var rechargeInput = document.querySelector(
        'input[type="radio"][name="purchaseOption"][value="subscription"]'
      );
      if (rechargeInput) {
        var rechargeLabel = rechargeInput.closest('label.rc-purchase-option__label, label');
        if (rechargeLabel) return rechargeLabel;
        return rechargeInput;
      }

      // Also try the subscription option's wrapper directly
      var subOption = document.querySelector(
        '.rc-purchase-option__subscription label.rc-purchase-option__label, ' +
        '.rc-purchase-option__subscription [role="radio"]'
      );
      if (subOption) return subOption;

      for (var i = 0; i < roots.length; i++) {
        var root = roots[i];

        // 1. Direct selectors
        var direct = root.querySelector(
          'input[type="radio"][name="purchaseOption"][value="subscription"], ' +
          'input[type="radio"][value*="subscribe" i], ' +
          '[role="radio"][aria-label*="subscribe" i], ' +
          'button[aria-label*="subscribe" i], ' +
          '[data-recharge-mode="subscription"], ' +
          '[data-rc-option="subscription"], ' +
          '[data-selling-plan-id]:not([data-selling-plan-id=""])'
        );
        if (direct) return direct;

        // 2. Find ANY element whose entire visible text equals "Subscribe & save"
        //    (or close variants). Match the most specific element (smallest textContent).
        var allElements = root.querySelectorAll('*');
        var bestMatch = null;
        var bestMatchLength = Infinity;
        for (var j = 0; j < allElements.length; j++) {
          var el = allElements[j];
          var text = (el.textContent || '').trim().toLowerCase();
          if (!text) continue;
          var isExact = (
            text === 'subscribe & save' ||
            text === 'subscribe and save' ||
            text === 'subscribe to save' ||
            text === 'subscribe' ||
            text === 'subscribe & save 10%' ||
            text === 'subscribe and save 10%'
          );
          // Prefer smallest matching element (most specific)
          if (isExact && text.length < bestMatchLength) {
            bestMatch = el;
            bestMatchLength = text.length;
          }
        }
        if (bestMatch) return walkUpToClickable(bestMatch, root);

        // 3. Looser match: contains "subscribe" but is reasonably short
        for (var k = 0; k < allElements.length; k++) {
          var el2 = allElements[k];
          var text2 = (el2.textContent || '').trim().toLowerCase();
          if (text2.length > 0 && text2.length < 40 && text2.indexOf('subscribe') > -1) {
            return walkUpToClickable(el2, root);
          }
        }

        // 4. Fallback: first input radio inside the recharge widget
        var firstRadio = root.querySelector('input[type="radio"]');
        if (firstRadio) return firstRadio;
      }

      // 5. GLOBAL fallback — search the entire document.body for "Subscribe & save"
      // (Recharge may render its widget into a portal outside the recharge container)
      var allDocEls = document.body.querySelectorAll('*');
      var bestMatch = null;
      var bestLength = Infinity;
      for (var n = 0; n < allDocEls.length; n++) {
        var de = allDocEls[n];
        // Skip my own pill button
        if (de.dataset && de.dataset.twhPillClick !== undefined) continue;
        var txt = (de.textContent || '').trim().toLowerCase();
        if (!txt) continue;
        if ((txt === 'subscribe & save' || txt === 'subscribe and save' ||
             txt === 'subscribe & save 10%' || txt === 'subscribe and save 10%') &&
            txt.length < bestLength) {
          bestMatch = de;
          bestLength = txt.length;
        }
      }
      if (bestMatch) {
        console.log('[twh] findRechargeSubscribe: matched globally:', bestMatch);
        return walkUpToClickable(bestMatch, document.body);
      }

      return null;
    }

    function trySelectSubscribe() {
      console.log('[twh] trySelectSubscribe: starting');

      // STRATEGY 1 (most reliable): find the exact Recharge subscription radio
      // and toggle it via the most thorough possible event chain.
      // Use deep search to pierce shadow DOM (Recharge v2 uses LIT web components).
      var subInput = deepQuerySelector(
        'input[type="radio"][name="purchaseOption"][value="subscription"]'
      );
      // Also discover shadow hosts for diagnostics
      var shadowHosts = deepFindAllShadowHosts();
      console.log('[twh] shadow hosts on page:', shadowHosts.length, shadowHosts.slice(0, 5));

      if (subInput) {
        console.log('[twh] strategy 1: found Recharge subscription radio (deep):', subInput);
        console.log('[twh] strategy 1: radio.checked BEFORE:', subInput.checked);

        try {
          // a) Click the LABEL — native browser behavior toggles the radio
          //    AND fires synthesized events that Recharge listens to.
          var label = subInput.closest('label');
          if (label) {
            console.log('[twh] strategy 1a: calling label.click()');
            label.click();
          }

          // b) Directly set checked + dispatch change event (covers cases where
          //    Recharge listens to change rather than click).
          if (!subInput.checked) {
            console.log('[twh] strategy 1b: setting input.checked = true');
            subInput.checked = true;
          }

          // c) Dispatch a full event chain. composed:true crosses shadow DOM.
          ['input', 'change'].forEach(function(evtType) {
            try {
              var ev = new Event(evtType, { bubbles: true, cancelable: true, composed: true });
              subInput.dispatchEvent(ev);
              console.log('[twh] strategy 1c: dispatched', evtType, 'on input');
            } catch (e) {
              console.warn('[twh] strategy 1c: failed to dispatch', evtType, e);
            }
          });

          // d) Also call input.click() directly (in case label click was blocked)
          console.log('[twh] strategy 1d: calling input.click()');
          subInput.click();

          console.log('[twh] strategy 1: radio.checked AFTER:', subInput.checked);
          return true;
        } catch (e) {
          console.error('[twh] strategy 1 failed:', e);
        }
      } else {
        console.warn('[twh] strategy 1: input[name="purchaseOption"][value="subscription"] NOT found');
      }

      // STRATEGY 2: fallback to the broader finder
      var subEl = findRechargeSubscribeElement();
      console.log('[twh] strategy 2: findRechargeSubscribeElement returned:', subEl);

      if (subEl) {
        try {
          ['pointerdown', 'mousedown', 'mouseup', 'click', 'change', 'input'].forEach(function(evt) {
            try {
              var ev;
              if (evt === 'click' || evt === 'pointerdown' || evt === 'mousedown' || evt === 'mouseup') {
                ev = new MouseEvent(evt, { bubbles: true, cancelable: true, composed: true, view: window });
              } else {
                ev = new Event(evt, { bubbles: true, composed: true });
              }
              subEl.dispatchEvent(ev);
            } catch (e) {}
          });
          if (typeof subEl.click === 'function') subEl.click();
          console.log('[twh] strategy 2: dispatched click chain');
          return true;
        } catch (e) {
          console.error('[twh] strategy 2 failed:', e);
        }
      }

      console.warn('[twh] all strategies failed — pill click did not toggle Recharge');
      return false;
    }

    // Expose debug helpers on window so we can iterate via Chrome console.
    window.twhDebug = {
      findRechargeSubscribe: findRechargeSubscribeElement,
      trySelectSubscribe: trySelectSubscribe,
      trySelectFourPack: trySelectFourPack,
      deepQuery: deepQuerySelector,
      shadowHosts: deepFindAllShadowHosts,
      findSubInputDeep: function() {
        return deepQuerySelector('input[type="radio"][name="purchaseOption"][value="subscription"]');
      },
      sync: sync,
      currentState: function() {
        return {
          variantId: getCurrentVariantId(),
          sellingPlan: getAnySellingPlanValue(),
          priceText: priceEl ? priceEl.textContent : null,
          stickerActive: stickerEl ? stickerEl.dataset.active : null,
          nudgeActive: otpNudgeEl ? otpNudgeEl.dataset.active : null,
          subscribeEl: findRechargeSubscribeElement()
        };
      },

      // Enumerates every element on the page that could be the Subscribe option,
      // plus iframe/shadow-DOM detection. Run from console and paste output.
      findAllSubscribeCandidates: function() {
        var results = { textMatches: [], iframes: [], shadowRoots: [], rechargeBlocks: [] };

        // Match elements containing "subscribe" in their visible text (short text only)
        document.body.querySelectorAll('*').forEach(function(el) {
          if (el.dataset && el.dataset.twhPillClick !== undefined) return;
          var text = (el.textContent || '').trim();
          if (text.length === 0 || text.length > 60) return;
          if (text.toLowerCase().indexOf('subscribe') === -1) return;
          results.textMatches.push({
            text: text,
            tag: el.tagName,
            id: el.id || null,
            classes: typeof el.className === 'string' ? el.className : null,
            cursor: window.getComputedStyle(el).cursor,
            role: el.getAttribute('role'),
            ariaLabel: el.getAttribute('aria-label'),
            dataAttrs: Object.keys(el.dataset || {}),
            parentTag: el.parentElement ? el.parentElement.tagName : null,
            parentId: el.parentElement ? el.parentElement.id : null,
            parentClass: el.parentElement && typeof el.parentElement.className === 'string' ? el.parentElement.className : null
          });
        });

        // Detect any iframes
        document.querySelectorAll('iframe').forEach(function(iframe) {
          results.iframes.push({
            src: iframe.src,
            id: iframe.id || null,
            classes: iframe.className || null,
            sameOrigin: (function() {
              try { return !!iframe.contentDocument; } catch (e) { return false; }
            })()
          });
        });

        // Detect any open shadow roots
        document.body.querySelectorAll('*').forEach(function(el) {
          if (el.shadowRoot) {
            results.shadowRoots.push({
              tag: el.tagName,
              id: el.id || null,
              classes: typeof el.className === 'string' ? el.className : null
            });
          }
        });

        // Recharge-class elements + their direct children summary
        document.querySelectorAll('[class*="recharge"], [id*="recharge"]').forEach(function(el) {
          var childTags = [];
          for (var i = 0; i < Math.min(el.children.length, 10); i++) {
            childTags.push(el.children[i].tagName + (el.children[i].id ? '#' + el.children[i].id : ''));
          }
          results.rechargeBlocks.push({
            tag: el.tagName,
            id: el.id || null,
            classes: typeof el.className === 'string' ? el.className : null,
            childCount: el.children.length,
            firstChildren: childTags,
            innerHtmlLen: el.innerHTML ? el.innerHTML.length : 0
          });
        });

        console.log('=== twh subscribe candidates ===');
        console.log(JSON.stringify(results, null, 2));
        console.log('=== end ===');
        return results;
      }
    };

    var pillEl = document.querySelector('[data-twh-pill-click]');
    if (pillEl) {
      pillEl.addEventListener('click', function(e) {
        e.preventDefault();
        console.log('[twh] pill clicked — starting subscribe flow');
        // Blur immediately — prevents the aria-hidden-on-focused-element a11y
        // violation when sync() later hides this button.
        try { pillEl.blur(); } catch (err) {}
        trySelectFourPack();
        trySelectSubscribe();
        // Re-sync after a frame and again after Recharge has a moment to mount its state change
        requestAnimationFrame(sync);
        setTimeout(sync, 120);
        setTimeout(sync, 500);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
